import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useSetAtom } from 'jotai'
import { apis } from '@modules/apiClients'
import { UserService } from '@duck/api-definitions'
import { setUserAtom, accessTokenAtom } from '@/store/userAtoms'
import { toast } from 'sonner'
import { ResendVerificationForm } from '../ResendVerificationForm'
import { StandardLoader } from '../StandardLoader'
import { useTar } from '@/modules'
import { Button, Card, CardContent, CardHeader, CardTitle } from '@duck/ui'
import { identifyUserInSentry } from '@/instrument.sentry'

interface VerificationState {
  status: 'loading' | 'success' | 'error' | 'resending'
  message: string
}

interface VerifyEmailProps {
  token: string
}

export const VerifyEmail = ({ token }: VerifyEmailProps) => {
  const navigate = useNavigate()
  const { t } = useTar()
  const setUser = useSetAtom(setUserAtom)
  const setAccessToken = useSetAtom(accessTokenAtom)
  const [state, setState] = useState<VerificationState>({
    status: 'loading',
    message: t('verification.verifying')
  })
  const [showResendForm, setShowResendForm] = useState(false)

  // Refs for debounce logic
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasVerifiedRef = useRef(false)


  const debouncedVerifyEmail = useCallback(async (tokenToVerify: string) => {
    setState({
      status: 'loading',
      message: t('verification.verifying')
    })

    try {
      const verifyRequest: UserService.VerifyEmailRequest = { token: tokenToVerify }
      const { data } = await apis.userService.authVerifyEmailPost(verifyRequest)

      setState({
        status: 'success',
        message: t('verification.success')
      })

      setUser(data.user)
      setAccessToken(data.accessToken)
      // Identify user in Sentry
      identifyUserInSentry(data.user)
      toast.success(t('verification.successToast'))

      setTimeout(() => {
        navigate({ to: '/challenges/available' })
      }, 3000)

    } catch (error: unknown) {
      const errorMessage = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || t('verification.verificationFailed')
      setState({
        status: 'error',
        message: errorMessage
      })

      toast.error(t('verification.verificationFailed'))
    }
  }, [t, setUser, setAccessToken, navigate])

  useEffect(() => {
    if (!token) {
      setState({
        status: 'error',
        message: t('verification.noToken')
      })
      return
    }

    if (hasVerifiedRef.current) {
      return
    }

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }

    debounceTimeoutRef.current = setTimeout(() => {
      hasVerifiedRef.current = true
      debouncedVerifyEmail(token)
    }, 1200)

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [token, debouncedVerifyEmail, t])

  const handleResendVerification = () => {
    setShowResendForm(true)
  }

  const handleResendSuccess = () => {
    hasVerifiedRef.current = false
    setShowResendForm(false)

    setTimeout(() => {
      navigate({ to: '/' })
    }, 3000)
  }

  const handleResendCancel = () => {
    setShowResendForm(false)
  }

  const getStatusColor = () => {
    switch (state.status) {
      case 'success':
        return 'text-success'
      case 'error':
        return 'text-error'
      case 'resending':
        return 'text-accent'
      default:
        return 'text-muted-foreground'
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md">

        <Card>
          <CardHeader className="text-center">
            <CardTitle>{t('verification.title')}</CardTitle>
          </CardHeader>

          <CardContent className="space-y-6">
            {(state.status === 'loading' || state.status === 'resending') && (
              <div className="flex justify-center">
                <StandardLoader />
              </div>
            )}

            <p className={`text-lg text-center ${getStatusColor()}`}>
              {state.message}
            </p>

            {state.status === 'success' && (
              <div className="bg-success/10 border border-success/20 rounded-lg p-4">
                <p className="text-sm text-center text-success">
                  {t('verification.redirecting')}
                </p>
              </div>
            )}

            {state.status === 'error' && (
              <div className="space-y-3">

                {!showResendForm ? (
                  <Button
                    onClick={handleResendVerification}
                    variant="secondary"
                    className="w-full"
                  >
                    {t('verification.requestNewVerificationLink')}
                  </Button>
                ) : (
                  <ResendVerificationForm
                    onSuccess={handleResendSuccess}
                    onCancel={handleResendCancel}
                  />
                )}
              </div>
            )}

            <Button
              onClick={() => navigate({ to: '/' })}
              variant="outline"
              className="w-full"
            >
              {t('common.goHome')}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
